import type { MailFolder, SpecialUse } from "./types.js";

/**
 * Gedeelde ordening van de mappenboom.
 *
 * Stond eerder in FolderList.tsx. Zodra de "Verplaatsen naar…"-kiezer dezelfde
 * boom moet tonen, mag die logica niet gedupliceerd worden: het hijsen van
 * speciale mappen hieronder is subtiel genoeg dat twee kopieën gegarandeerd uit
 * elkaar lopen, en dan staat Verzonden in de zijbalk ergens anders dan in de
 * kiezer.
 */

/** Diepte in inspringing; dieper dan vier niveaus wordt onleesbaar smal. */
export function indentFor(depth: number): string {
  return `${12 + Math.min(depth, 4) * 14}px`;
}

/** Outlook-volgorde voor de speciale mappen bovenaan. */
const SPECIALE_VOLGORDE: readonly NonNullable<SpecialUse>[] = [
  "inbox", "drafts", "sent", "archive", "junk", "trash",
];

export interface Row {
  folder: MailFolder;
  /** Weergavediepte; bij gehesen subbomen genormaliseerd naar de nieuwe wortel. */
  depth: number;
}

/**
 * Hijst de speciale mappen (mét hun subbomen) naar boven, in vaste volgorde,
 * en laat de rest in de serverorde staan.
 *
 * Waarom niet gewoon sorteren op specialUse: bij servers met dot-notatie zijn
 * Verzonden en Concepten kinderen van INBOX ("INBOX.Sent"). Een vlakke sort
 * rukt submappen los van hun ouder en dan klopt de inspringing nergens meer.
 * Daarom nemen we per speciale map de hele subboom mee (opeenvolgende regels
 * met een grotere diepte) en normaliseren we de diepte naar de nieuwe plek.
 */
export function orden(folders: MailFolder[]): { special: Row[]; rest: Row[] } {
  const meegenomen = new Set<string>();
  const special: Row[] = [];

  for (const soort of SPECIALE_VOLGORDE) {
    const start = folders.findIndex((f) => f.specialUse === soort && !meegenomen.has(f.path));
    if (start === -1) continue;

    const wortel = folders[start];
    if (wortel === undefined) continue;
    special.push({ folder: wortel, depth: 0 });
    meegenomen.add(wortel.path);

    for (let i = start + 1; i < folders.length; i++) {
      const kind = folders[i];
      if (kind === undefined || kind.depth <= wortel.depth) break;
      // Geneste mappen met een eigen specialUse krijgen hun eigen plek in de
      // vaste volgorde; die slaan we hier over.
      if (kind.specialUse !== null) continue;
      special.push({ folder: kind, depth: kind.depth - wortel.depth });
      meegenomen.add(kind.path);
    }
  }

  const rest: Row[] = folders
    .filter((f) => !meegenomen.has(f.path))
    .map((f) => ({ folder: f, depth: f.depth }));

  return { special, rest };
}

/**
 * Ligt `path` onder `ancestor`? Met de delimiter erbij, want zonder zou
 * "INBOX.Werkmap" ook onder "INBOX.Werk" lijken te vallen.
 *
 * De weergavediepte uit `orden()` is hier onbruikbaar: die is genormaliseerd
 * voor het hijsen van speciale mappen en zegt niets over de echte
 * ouder-kindrelatie in IMAP.
 */
export function isDescendant(ancestor: MailFolder, path: string): boolean {
  return path.startsWith(`${ancestor.path}${ancestor.delimiter}`);
}

/**
 * Zet een pad om na het hernoemen of verplaatsen van een map. Geeft `null`
 * terug als het pad niets met de hernoemde map te maken had.
 */
export function remapPath(
  path: string,
  previousPath: string,
  nextPath: string,
  delimiter: string,
): string | null {
  if (path === previousPath) return nextPath;
  const prefix = `${previousPath}${delimiter}`;
  if (path.startsWith(prefix)) return nextPath + path.slice(previousPath.length);
  return null;
}
